import {
  VORWARN_STANDARD_TAGE,
  VORWARN_TAGE_MAX,
  VORWARN_TAGE_OPTIONEN,
  vorwarnZeitText,
  vorwarnungBeschreiben,
} from '@netcon-work/shared'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { cn } from '../lib/cn'
import { meineVorwarnungQuery } from '../lib/queries'
import { trpc } from '../lib/trpc'

/**
 * **„Benachrichtigen" – der Wecker an einer einzelnen Aufgabe**
 * ([Z-13](../../../../docs/phasen/zusatz-13-frist-vorwarnung.md)).
 *
 * Nils, 28.08.2026: *„Wenn ich das bspw. bei einer bestimmten Aufgabe anhake ‚Benachrichtigen',
 * dann gilt für mich die Einstellung bspw. ‚3 Tage vorher' … wenn es jemand anders da einträgt
 * – dann gilt das nur für ihn. Zusätzlich würde ich anbieten: Benachrichtigung an alle
 * Bearbeiter."*
 *
 * ## Warum Chips und keine Häkchen (28.08.2026, nachgebessert)
 *
 * Die erste Fassung hatte zwei nackte Checkboxen und daneben den Satz „· 3 Tage vorher, per Mail
 * und im System". Nils, mit einem Bild davon: *„uff sieht das fiess aus … ich wollte das schön
 * optisch haben, das man das optional anklicken kann."* Und er hatte recht – der Fehler war
 * handwerklich: langer Fließtext in einer Spalte von 112 px Restbreite bricht dreizeilig um und
 * drängt sich neben das Kästchen.
 *
 * Ein Chip löst beides. Er ist **kurz** (bricht also nicht), er ist als Knopf erkennbar, und er
 * trägt in sich die Antwort auf *„und was passiert dann?"* – nämlich die eingestellte
 * Vorwarnzeit. Der Rest steht im Titel; ein Aufgabendetail ist eine Auskunft, kein Formular
 * (dieselbe Überlegung wie bei `InlineWert`).
 *
 * ## Warum hier keine Tageszahl einstellbar ist
 *
 * Weil sie **an der Person** steht und für alle Abos zugleich gilt (unten links neben dem
 * eigenen Namen). Ein Feld je Aufgabe wäre die naheliegende Variante und die falsche: zwei
 * Leute, die dieselbe Aufgabe im Blick behalten wollen, überschrieben sich gegenseitig die
 * Frist. Der Chip **zeigt** sie deshalb, er setzt sie nicht.
 *
 * ## Der zweite Chip erscheint erst, wenn der erste an ist
 *
 * „Auch alle Bearbeiter" ohne „Erinnern" wäre eine Einstellung ohne Wirkung. Dauerhaft
 * angezeigt wäre sie in neun von zehn Aufgaben ein ausgegrauter Knopf.
 */
export function BenachrichtigenZeile({ taskId }: { taskId: string }) {
  const queryClient = useQueryClient()
  const { data } = useQuery(meineVorwarnungQuery)

  const eintrag = data?.aufgaben.find((a) => a.taskId === taskId)
  const betrifftMich = eintrag !== undefined
  const eigenesAbo = eintrag?.eigenesAbo ?? false
  const alleBearbeiter = eintrag?.alleBearbeiter ?? false
  /**
   * **Meine Frist für genau diese Aufgabe** ([E-324](../../../../docs/ENTSCHEIDUNGEN.md#e-324),
   * 04.09.2026) – `null` heißt „meine Voreinstellung", und das ist der Normalfall.
   */
  const eigeneTage = eintrag?.tage ?? null
  const tage = eigeneTage ?? data?.einstellung.tage ?? VORWARN_STANDARD_TAGE
  const [wahlOffen, setWahlOffen] = useState(false)

  /** Beide Kanäle aus heißt: das Abo steht, aber es passiert nichts. Das muss man sehen. */
  const stumm = data !== undefined && !data.einstellung.perMail && !data.einstellung.imSystem

  /**
   * **Optimistisch wie alles andere** (Leitplanke 1): der Chip springt sofort um. Auf eine
   * Serverantwort für einen Klick zu warten, den man selbst gerade getan hat, wäre der
   * Spinner, den es hier nicht geben darf.
   */
  const setzen = useMutation({
    mutationFn: (wert: { an: boolean; alleBearbeiter: boolean; tage?: number | null }) =>
      trpc.vorwarnung.setzen.mutate({ taskId, ...wert }),
    onMutate: async (wert) => {
      await queryClient.cancelQueries({ queryKey: meineVorwarnungQuery.queryKey })
      const vorher = queryClient.getQueryData(meineVorwarnungQuery.queryKey)
      queryClient.setQueryData(meineVorwarnungQuery.queryKey, (alt) =>
        alt === undefined
          ? alt
          : {
              ...alt,
              aufgaben: wert.an
                ? [
                    ...alt.aufgaben.filter((a) => a.taskId !== taskId),
                    {
                      taskId,
                      eigenesAbo: true,
                      alleBearbeiter: wert.alleBearbeiter,
                      /** Weggelassen heißt „nicht anfassen" – wie beim Server (E-324). */
                      tage: wert.tage !== undefined ? wert.tage : eigeneTage,
                    },
                  ]
                : alt.aufgaben.filter((a) => a.taskId !== taskId),
            },
      )
      return { vorher }
    },
    onError: (_fehler, _wert, kontext) => {
      queryClient.setQueryData(meineVorwarnungQuery.queryKey, kontext?.vorher)
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: meineVorwarnungQuery.queryKey }),
  })

  return (
    <div className="flex gap-2 border-b border-line py-1 last:border-b-0">
      <dt className="w-[112px] shrink-0 text-muted">Erinnerung</dt>
      <dd className="min-w-0 flex-1">
        {/* `flex-wrap`: bei zwei Chips und schmalem Fenster rutscht der zweite in die
            nächste Zeile, statt aus dem Bild zu laufen (Z-10). */}
        <div className="flex flex-wrap items-center gap-1.5">
          {/**
           * **Der Chip schaltet an und aus, der Klick auf die Zeit wählt sie**
           * ([E-324](../../../../docs/ENTSCHEIDUNGEN.md#e-324), 04.09.2026).
           *
           * Nils: *„ich wollte doch, dass die Benachrichtigung – also das Erinnern – flexibel
           * gewählt werden kann."* Bis hierher stand die Zahl nur unten links bei seinem Namen
           * und galt für **alle** Aufgaben; am Chip war sie zu lesen, nicht zu ändern. Er hat
           * sie deshalb nie gefunden – seine Einstellung stand noch auf dem Standardwert.
           */}
          <Chip
            an={eigenesAbo}
            aria-label={eigenesAbo ? 'Erinnerung abbestellen' : 'An diesen Termin erinnern'}
            titel={
              !data
                ? 'An diesen Termin erinnern'
                : eigenesAbo
                  ? `Kommt ${vorwarnungBeschreiben({ ...data.einstellung, tage }).toLowerCase()}.`
                  : 'Erinnert dich vor dem Termin – wie lange vorher, wählst du gleich hier.'
            }
            onClick={() => setzen.mutate({ an: !eigenesAbo, alleBearbeiter })}
          >
            🔔 {eigenesAbo ? vorwarnZeitText(tage) : 'Erinnern'}
          </Chip>

          {eigenesAbo && (
            <button
              type="button"
              onClick={() => setWahlOffen((o) => !o)}
              aria-expanded={wahlOffen}
              className="cursor-pointer text-[11px] text-accent-deep hover:underline"
              title="Wie lange vorher – nur für diese Aufgabe und nur für dich"
            >
              {wahlOffen ? 'fertig' : 'ändern'}
            </button>
          )}

          {eigenesAbo && (
            <Chip
              an={alleBearbeiter}
              aria-label={
                alleBearbeiter ? 'Nur mich benachrichtigen' : 'Auch alle Bearbeiter benachrichtigen'
              }
              titel="Jeder Bearbeiter dieser Aufgabe wird erinnert – jeder nach seiner eigenen Vorwarnzeit."
              onClick={() => setzen.mutate({ an: true, alleBearbeiter: !alleBearbeiter })}
            >
              👥 alle Bearbeiter
            </Chip>
          )}
        </div>

        {/**
         * **Die Wahl der Zeit – hier, nicht unten links** (E-324).
         *
         * Dieselben Stufen wie in der Voreinstellung plus ein freies Feld: eine zweite Auswahl
         * mit anderen Zahlen wäre der Anfang von „warum kann ich hier nicht, was ich dort
         * kann". Und „wie voreingestellt" steht als eigener Knopf da, damit man eine einzelne
         * Abweichung auch wieder loswird, ohne die Zahl zu erraten.
         */}
        {wahlOffen && (
          <div className="mt-1.5 flex flex-wrap items-center gap-1">
            {VORWARN_TAGE_OPTIONEN.map((wert) => (
              <button
                key={wert}
                type="button"
                aria-label={vorwarnZeitText(wert)}
                onClick={() => setzen.mutate({ an: true, alleBearbeiter, tage: wert })}
                className={cn(
                  'cursor-pointer rounded-md border px-2 py-[2px] text-[11px]',
                  eigeneTage === wert
                    ? 'border-accent bg-accent-soft text-accent-deep'
                    : 'border-line2 bg-surface text-ink2 hover:bg-page',
                )}
              >
                {wert === 0 ? 'am Tag' : `${wert} T`}
              </button>
            ))}
            <label className="flex items-center gap-1 text-[11px] text-muted">
              <span>oder</span>
              <input
                type="number"
                min={0}
                max={VORWARN_TAGE_MAX}
                defaultValue={eigeneTage ?? ''}
                aria-label={`Eigene Zeit in Tagen, 0 bis ${VORWARN_TAGE_MAX}`}
                onBlur={(e) => {
                  const zahl = Number(e.target.value)
                  /** Ungültiges wird verworfen, nicht gerundet – wie in der Voreinstellung. */
                  if (e.target.value.trim() === '' || !Number.isInteger(zahl)) return
                  if (zahl < 0 || zahl > VORWARN_TAGE_MAX) return
                  if (zahl !== eigeneTage) setzen.mutate({ an: true, alleBearbeiter, tage: zahl })
                }}
                className="w-[46px] rounded-md border border-line2 bg-surface px-1 py-[2px] text-[11px]"
              />
              <span>Tage</span>
            </label>
            {eigeneTage !== null && (
              <button
                type="button"
                onClick={() => setzen.mutate({ an: true, alleBearbeiter, tage: null })}
                className="cursor-pointer text-[11px] text-muted hover:text-accent-deep hover:underline"
              >
                wie voreingestellt (
                {vorwarnZeitText(data?.einstellung.tage ?? VORWARN_STANDARD_TAGE).toLowerCase()})
              </button>
            )}
          </div>
        )}

        {/**
         * **Zwei Sätze, die es nur im Ausnahmefall gibt** – und beide erklären etwas, das man
         * sonst für einen Fehler hält:
         *
         * 1. Ich werde erinnert, obwohl mein Chip aus ist – weil ein anderer „alle Bearbeiter"
         *    gesetzt hat. Der Schalter dafür gehört ihm.
         * 2. Mein Chip ist an, aber ich habe beide Kanäle abgeschaltet. Ohne den Hinweis sucht
         *    man den Fehler beim Häkchen statt bei der eigenen Einstellung.
         */}
        {betrifftMich && !eigenesAbo && (
          <p className="mt-0.5 text-[11px] text-muted">
            Du wirst erinnert, weil jemand die Bearbeiter benachrichtigen lässt.
          </p>
        )}
        {eigenesAbo && stumm && (
          <p className="mt-0.5 text-[11px] text-warn">
            Beide Kanäle sind aus – unten links bei deinem Namen einschalten.
          </p>
        )}
      </dd>
    </div>
  )
}

/**
 * **Ein Chip, der an und aus kennt.**
 *
 * Nicht `Chip` aus den Primitives: der ist ein `span` ohne Zustand – eine Marke, kein Schalter.
 * Die Farben sind dieselben wie bei den Wahlknöpfen in der Seitenleiste (`ErinnerungsWahl`),
 * damit „ausgewählt" im ganzen Programm gleich aussieht.
 */
function Chip({
  an,
  titel,
  onClick,
  children,
  'aria-label': ariaLabel,
}: {
  an: boolean
  titel: string
  onClick: () => void
  children: React.ReactNode
  'aria-label': string
}) {
  return (
    <button
      type="button"
      aria-pressed={an}
      aria-label={ariaLabel}
      title={titel}
      onClick={onClick}
      className={cn(
        'inline-flex cursor-pointer items-center gap-1 rounded-full border px-[10px] py-[3px] text-[11.5px] whitespace-nowrap transition-colors',
        an
          ? 'border-accent bg-accent-soft font-semibold text-accent-deep'
          : 'border-line2 bg-surface text-muted hover:bg-page hover:text-ink2',
      )}
    >
      {children}
    </button>
  )
}
